import { IUnAssignedOrdersHook } from '@models';
import { UnassignedOrderKind, UnassignedSearchField } from '@services';
import { useStore } from '@stores';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** What the user has typed but not yet asked for. */
interface SearchDraft {
  /** Which column `text` is aimed at - the card's identity: vehicle, customer or build. */
  field: UnassignedSearchField;
  text: string;
  /** The work itself. Its own field so it narrows whatever the box above already found. */
  title: string;
  status: string[];
}

const EMPTY_DRAFT: SearchDraft = {
  field: 'registrationNumber',
  text: '',
  title: '',
  status: [],
};

export const useUnAssignedServiceOrders = (
  mobile = false
): IUnAssignedOrdersHook & { loadMore: () => void; hasMore: boolean } => {
  const { orderStore, authenticationStore } = useStore();
  const {
    unAssignedItems,
    unAssignedTotalCount,
    unAssignedSkipCount,
    unAssignedLoading,
    unAssignedMaxResultCount,
    setUnAssignedSkipCount,
    setUnassignedPageChange,
  } = orderStore;

  const iAmManager =
    (authenticationStore.currentUser?.role &&
      ['Manager', 'ManagerTechnician'].includes(authenticationStore.currentUser.role)) ||
    false;

  const [kind, setKindValue] = useState<UnassignedOrderKind>('All');

  // Two copies on purpose. The draft moves with every keystroke and every dropdown; only what has
  // been searched for reaches the API, so picking a column - or typing half a registration - is
  // not a request. Kind is not in here: it is a mode, not a search, and applies straight away.
  const [draft, setDraft] = useState<SearchDraft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<SearchDraft>(EMPTY_DRAFT);

  const filters = useMemo(
    () => ({
      kind,
      status: applied.status.length ? applied.status.join(',') : null,
      title: applied.title || undefined,
      // The term goes into the one column it was aimed at, so the API filters that column and no
      // other. An empty term leaves the key undefined, which is no filter at all.
      [applied.field]: applied.text || undefined,
    }),
    [kind, applied]
  );

  const mobilePageSize = 3;
  const mobileSkip = useRef(0);
  const [mobileLoading, setMobileLoading] = useState(false);

  useEffect(() => {
    if (mobile) {
      mobileSkip.current = 0;
      orderStore.clearUnAssignedItems();
      iAmManager &&
        orderStore.getUnAssignedItems({ ...filters, maxResultCount: mobilePageSize, skipCount: 0 });
    } else {
      iAmManager &&
        orderStore.getUnAssignedItems({
          ...filters,
          maxResultCount: unAssignedMaxResultCount,
          skipCount: unAssignedSkipCount,
        });
    }

    // Results accumulate in a Map, so the previous set has to go before the next one lands -
    // otherwise a narrowed filter would still show everything it just excluded.
    return () => orderStore.clearUnAssignedItems();
  }, [
    orderStore,
    mobile ? null : unAssignedSkipCount,
    mobile ? null : unAssignedMaxResultCount,
    iAmManager,
    filters,
  ]);

  const items = Array.from(unAssignedItems.values());
  const hasMore = items.length < unAssignedTotalCount;

  const loadMore = useCallback(() => {
    if (!mobile || mobileLoading || !hasMore) return;
    const nextSkip = mobileSkip.current + mobilePageSize;
    mobileSkip.current = nextSkip;
    setMobileLoading(true);
    orderStore
      .getUnAssignedItems({ ...filters, maxResultCount: mobilePageSize, skipCount: nextSkip })
      .then(() => setMobileLoading(false));
  }, [mobile, mobileLoading, hasMore, orderStore, filters]);

  /**
   * Every search goes back to the first page, or a narrowed list lands on a page that is now
   * empty. Through setUnassignedPageChange rather than setUnAssignedSkipCount, because the
   * store keeps a page number alongside the skip count and the two must not disagree.
   */
  const resetPaging = useCallback(() => {
    mobileSkip.current = 0;
    setUnassignedPageChange(1);
  }, [setUnassignedPageChange]);

  /** Switching column drops the term with it: the box holds one filter, not one per field. */
  const setSearchField = useCallback(
    (field: UnassignedSearchField) => setDraft((d) => ({ ...d, field, text: '' })),
    []
  );

  const setSearchText = useCallback((text: string) => setDraft((d) => ({ ...d, text })), []);
  const setTitle = useCallback((title: string) => setDraft((d) => ({ ...d, title })), []);
  const setStatus = useCallback((status: string[]) => setDraft((d) => ({ ...d, status })), []);

  const applySearch = useCallback(() => {
    setApplied({ ...draft, text: draft.text.trim(), title: draft.title.trim() });
    resetPaging();
  }, [draft, resetPaging]);

  const clearSearch = useCallback(() => {
    setDraft((d) => ({ ...EMPTY_DRAFT, field: d.field }));
    setApplied((a) => ({ ...EMPTY_DRAFT, field: a.field }));
    resetPaging();
  }, [resetPaging]);

  const setKind = useCallback(
    (value: UnassignedOrderKind) => {
      setKindValue(value);
      // Nothing to search a build by once builds are excluded, so an applied build filter would
      // silently keep narrowing a list that can no longer contain any.
      if (value === 'Regular') {
        setDraft((d) =>
          d.field === 'build' ? { ...d, field: 'registrationNumber', text: '' } : d
        );
        setApplied((a) =>
          a.field === 'build' ? { ...a, field: 'registrationNumber', text: '' } : a
        );
      }
      resetPaging();
    },
    [resetPaging]
  );

  const hasSearch = !!(
    applied.text ||
    applied.title ||
    applied.status.length ||
    draft.text ||
    draft.title ||
    draft.status.length
  );

  return {
    unAssignedItems: items,
    unAssignedMaxResultCount: mobile ? mobilePageSize : unAssignedMaxResultCount,
    unAssignedSkipCount: mobile ? mobileSkip.current : unAssignedSkipCount,
    unAssignedLoading: mobile ? mobileLoading : unAssignedLoading,
    unAssignedTotalCount,
    setUnAssignedSkipCount,
    setUnassignedPageChange,
    kind,
    setKind,
    searchField: draft.field,
    setSearchField,
    searchText: draft.text,
    setSearchText,
    title: draft.title,
    setTitle,
    status: draft.status,
    setStatus,
    applySearch,
    clearSearch,
    hasSearch,
    loadMore,
    hasMore,
  };
};
